import { useState, useCallback, useEffect, lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import ScrollToTop from "./components/shared/ScrollToTop";
import LoadingScreen from "./components/shared/LoadingScreen";
import Index from "./pages/Index"; // Home eager para LCP
// Toaster: só é necessário depois da hidratação (usado no /contato).
// Lazy-load remove ~4 KB gzip do bundle inicial e reduz Script Evaluation.
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sobre = lazy(() => import("./pages/Sobre"));
const Contato = lazy(() => import("./pages/Contato"));
const PericiaPsicologica = lazy(() => import("./pages/servicos/PericiaPsicologica"));
const LaudosPsicologicos = lazy(() => import("./pages/servicos/empresas/LaudosPsicologicos"));
const AtestadoPsicosocial = lazy(() => import("./pages/servicos/empresas/AtestadoPsicosocial"));
const SaudeMentalNR1 = lazy(() => import("./pages/servicos/empresas/SaudeMentalNR1"));
const TerapiaEmpresas = lazy(() => import("./pages/servicos/empresas/TerapiaEmpresas"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const CasesCorporativos = lazy(() => import("./pages/CasesCorporativos"));
const GuiaNR1SaudeMental = lazy(() => import("./pages/blog/GuiaNR1SaudeMental"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Dispara o LoadingScreen a cada troca de rota
const RouteTransitionLoader = ({ onStart }: { onStart: () => void }) => {
  const { pathname } = useLocation();
  const [firstRender, setFirstRender] = useState(true);
  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    onStart();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const App = () => {
  // Não bloqueia a primeira pintura (LCP). LoadingScreen só reaparece em trocas de rota.
  const [loading, setLoading] = useState(false);
  const handleFinish = useCallback(() => setLoading(false), []);
  const handleStart = useCallback(() => setLoading(true), []);

  return (
  <TooltipProvider>
      <LazyMotion features={domAnimation} strict>
      {loading && <LoadingScreen onFinish={handleFinish} />}
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <RouteTransitionLoader onStart={handleStart} />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/contato" element={<Contato />} />
          
          {/* Serviços Principais */}
          <Route path="/servicos/pericia-psicologica" element={<PericiaPsicologica />} />
          
          {/* Para Empresas */}
          <Route path="/servicos/empresas/laudos-psicologicos" element={<LaudosPsicologicos />} />
          <Route path="/servicos/empresas/atestado-psicosocial" element={<AtestadoPsicosocial />} />
          <Route path="/servicos/empresas/saude-mental-nr1" element={<SaudeMentalNR1 />} />
          <Route path="/servicos/empresas/terapia-empresas" element={<TerapiaEmpresas />} />
          <Route path="/cases-corporativos" element={<CasesCorporativos />} />
          <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/blog/guia-adequacao-nr1-saude-mental" element={<GuiaNR1SaudeMental />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </LazyMotion>
  </TooltipProvider>
  );
};

export default App;
